import { useState, useCallback, useEffect } from "react";
import { fetchKoreanRSS, fetchYouTubeSources, generateBriefing, fetchPreBriefing, loadAnchorCacheWithTs, saveAnchorCache } from "../lib/anchor-api";

export default function useAnchor({ news, scenarios }) {
  const [directionKeyword, setDirectionKeyword] = useState("");

  const [briefing, setBriefing] = useState(() => loadAnchorCacheWithTs()?.data ?? null);
  const [briefingTs, setBriefingTs] = useState(() => loadAnchorCacheWithTs()?.ts ?? null);
  const [collectedSources, setCollectedSources] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // Fetch pre-generated briefing from worker on mount (if no local cache)
  useEffect(() => {
    if (briefing) return; // Already have a cached briefing
    let cancelled = false;
    (async () => {
      try {
        const pre = await fetchPreBriefing();
        if (!cancelled && pre) {
          setBriefing(pre.briefing);
          setBriefingTs(pre.ts);
          saveAnchorCache(pre.briefing);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-section loading states for progressive UI
  const [sourceStatuses, setSourceStatuses] = useState([]);
  const [phase, setPhase] = useState("idle"); // idle | collecting | analyzing | done

  const generate = useCallback(async () => {
    setGenerating(true);
    setError("");
    setBriefing(null);
    setSourceStatuses([]);
    setPhase("collecting");

    try {
      // 1. Collect sources in parallel
      const [rssResult, ytResult] = await Promise.allSettled([
        fetchKoreanRSS(),
        fetchYouTubeSources(),
      ]);

      const koreanArticles = rssResult.status === "fulfilled" ? rssResult.value.articles : [];
      const rssStatuses = rssResult.status === "fulfilled" ? rssResult.value.statuses : [{ source: "한국 RSS", status: "fail" }];

      const youtubeVideos = ytResult.status === "fulfilled" ? ytResult.value.videos : [];
      const ytStatuses = ytResult.status === "fulfilled" ? ytResult.value.statuses : [{ source: "YouTube", status: "fail" }];

      setCollectedSources({
        jtbc: youtubeVideos.filter(v => v.source === "이가혁라이브"),
        youtube: youtubeVideos.filter(v => v.source !== "이가혁라이브"),
        foreign: news.slice(0, 15),
        korean: koreanArticles,
      });
      const foreignStatus = { source: "외신 (Worker)", status: news.length > 0 ? "success" : "skip", count: news.length };
      const jtbcStatus = ytStatuses.find(s => s.source === "이가혁라이브");
      const restYt = ytStatuses.filter(s => s.source !== "이가혁라이브");
      setSourceStatuses([
        ...(jtbcStatus ? [jtbcStatus] : []),
        foreignStatus,
        ...rssStatuses,
        ...restYt,
      ]);

      // 2. Generate briefing
      setPhase("analyzing");
      const result = await generateBriefing({
        foreignNews: news,
        koreanArticles,
        youtubeVideos,
        scenarios,
        directionKeyword: directionKeyword.trim(),
      });

      setBriefing(result);
      setBriefingTs(Date.now());
      saveAnchorCache(result);
      setPhase("done");
    } catch (e) {
      const msg = e?.code === "daily_cap"
        ? "오늘 브리핑 생성 한도 6회를 모두 사용했습니다 (자정 리셋)"
        : e?.code === "rate_limit"
          ? "요청이 너무 빠릅니다. 잠시 후 다시 시도해 주세요"
          : (e.message || "브리핑 생성 실패");
      setError(msg);
      setPhase("idle");
    }
    setGenerating(false);
  }, [news, scenarios, directionKeyword]);

  return {
    directionKeyword, setDirectionKeyword,
    briefing, briefingTs, collectedSources, generating, error, sourceStatuses, phase,
    generate,
  };
}
